import { MutableRefObject, useEffect, useRef } from "react";

interface AudioVisualiserProps {
  callbackRef: MutableRefObject<VisualiserControls | null>;
  canvasWidth?: number;
  sampleLength: number;
}

/**
 * We expose these via a ref so they can be interacted with without waiting for re-renders.
 */
export interface VisualiserControls {
  start(analyser: AnalyserNode): void;
  toDataURL(): string | undefined;
  forceStopRecording(): void;
}

const AudioVisualiser = ({
  callbackRef,
  canvasWidth,
  sampleLength,
}: AudioVisualiserProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current) {
      let previousTimestamp: number = 0;
      canvasRef.current.width = canvasWidth || 300;

      let initialRender = true;
      let animationFrameId: any;
      let x: number = 0;

      const stopAnimation = () => {
        cancelAnimationFrame(animationFrameId);
        x = 0;
        previousTimestamp = 0;
        dataArray = undefined;
        initialRender = true;
      };

      let dataArray: Uint8Array | undefined;

      const draw = (timestamp: number, analyser: AnalyserNode) => {
        if (!previousTimestamp) {
          previousTimestamp = timestamp;
        }
        const elapsedTime = timestamp - previousTimestamp;
        previousTimestamp = timestamp;

        const width = canvasRef.current!.width;
        const height = canvasRef.current!.height;
        const canvasCtx = canvasRef.current!.getContext("2d")!;
        if (initialRender) {
          canvasCtx.moveTo(0, 0);
          canvasCtx.clearRect(0, 0, width, height);
          initialRender = false;
        }

        canvasCtx.lineWidth = 1;
        canvasCtx.strokeStyle = "#8884D8";
        canvasCtx.beginPath();

        if (dataArray) {
          const sliceWidth =
            ((elapsedTime / sampleLength) * width) / dataArray.length;
          for (let i = 0; i < dataArray.length; ++i) {
            const value = dataArray[i] / 128;
            const y = (value * height) / 2;
            canvasCtx.lineTo(x, y);
            x = x + sliceWidth;
          }
          canvasCtx.stroke();
        } else {
          dataArray = new Uint8Array(analyser.frequencyBinCount);
        }

        animationFrameId = requestAnimationFrame((timestamp) =>
          draw(timestamp, analyser)
        );

        // Used next frame.
        analyser.getByteTimeDomainData(dataArray);
        if (x > width || elapsedTime > sampleLength) {
          stopAnimation();
        }
      };

      callbackRef.current = {
        start(analyser) {
          animationFrameId = requestAnimationFrame((timestamp) =>
            draw(timestamp, analyser)
          );
        },
        toDataURL() {
          return canvasRef.current?.toDataURL();
        },
        forceStopRecording() {
          stopAnimation();
        },
      };

      return stopAnimation;
    }
  }, [canvasRef, canvasWidth, callbackRef, sampleLength]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: "100%",
        height: "150px",
        zIndex: 1,
        position: "relative",
      }}
    />
  );
};

export default AudioVisualiser;
